import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { ImSoundcloud2 } from "react-icons/im";
import { Pause, Play } from "lucide-react";
import { useT } from "@jammable/ui-core/src/i18n";

// Train page demo, design language F: the three steps of a real custom voice
// (training data -> input song -> final output). Sits under the upload step
// as a quiet proof that training works. One clip plays at a time. Labels are
// message keys (messages/train.ts).
const DEMOS = [
  {
    url: "https://audio.jammable.com/demos/trainingdata.mp3",
    label: "train.demo.data.label",
    note: "train.demo.data.note",
  },
  {
    url: "https://audio.jammable.com/demos/inputsong2.mp3",
    label: "train.demo.input.label",
    note: "train.demo.input.note",
  },
  {
    url: "https://audio.jammable.com/demos/finaloutput.mp3",
    label: "train.demo.output.label",
    note: "train.demo.output.note",
  },
];

const CustomModelDemo = () => {
  const t = useT();
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    return () => audioRef.current?.pause();
  }, []);

  const toggle = (idx) => {
    audioRef.current?.pause();
    if (playing === idx) {
      setPlaying(null);
      return;
    }
    const audio = new Audio(DEMOS[idx].url);
    audio.addEventListener("ended", () => setPlaying(null));
    audio.play().catch(() => setPlaying(null));
    audioRef.current = audio;
    setPlaying(idx);
  };

  return (
    <section className="tw-flex tw-flex-col tw-gap-3 tw-rounded-[28px] tw-bg-jam-lav tw-p-4 md:tw-p-5">
      <div className="tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-2">
        <h2 className="jam-display tw-m-0 tw-text-[20px] tw-font-extrabold tw-leading-none">
          {t("train.demo.title")}
        </h2>
        <div className="tw-text-xs tw-font-bold tw-text-jam-muted">
          {t("train.demo.by")}{" "}
          <Link
            href="https://soundcloud.com/skyrohket"
            className="tw-inline-flex tw-items-center tw-gap-1 tw-font-extrabold tw-text-jam-purple tw-no-underline"
            target="_blank"
          >
            SkyRohket
            <ImSoundcloud2 />
          </Link>
        </div>
      </div>

      <div className="tw-grid tw-grid-cols-1 tw-gap-2 md:tw-grid-cols-3">
        {DEMOS.map((demo, idx) => {
          const isPlaying = playing === idx;
          const isOutput = idx === DEMOS.length - 1;
          return (
            <button
              key={demo.url}
              type="button"
              onClick={() => toggle(idx)}
              aria-label={t(
                isPlaying ? "train.demo.pause" : "train.demo.play",
                {
                  label: t(demo.label),
                },
              )}
              className="tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-[20px] tw-border-0 tw-bg-white tw-p-2.5 tw-text-left tw-text-jam-ink tw-transition-shadow hover:tw-shadow-[0_0_0_2px_#CDB8EE]"
            >
              <span
                className={`tw-flex tw-h-10 tw-w-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full ${
                  isOutput
                    ? "tw-bg-jam-lime tw-text-jam-ink"
                    : "tw-bg-jam-ink tw-text-white"
                }`}
              >
                {isPlaying ? (
                  <Pause size={16} strokeWidth={2.75} fill="currentColor" />
                ) : (
                  <Play
                    size={16}
                    strokeWidth={2.75}
                    fill="currentColor"
                    className="tw-ml-0.5"
                  />
                )}
              </span>
              <span className="tw-min-w-0">
                <span className="tw-block tw-text-sm tw-font-extrabold">
                  {idx + 1}. {t(demo.label)}
                </span>
                <span className="tw-block tw-truncate tw-text-xs tw-font-semibold tw-text-jam-muted">
                  {t(demo.note)}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default CustomModelDemo;
